import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { ShoppingCart, Package, Minus, Plus, ArrowLeft, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => { setProduct(r.data); setLoading(false); }).catch(() => { setLoading(false); navigate('/tienda'); });
  }, [id, navigate]);

  if (loading) return <div className="py-20 text-center"><div className="animate-pulse text-muted-foreground">Cargando...</div></div>;
  if (!product) return null;

  const images = product.images || [];
  const currentImage = images[selectedImage];
  const imageUrl = currentImage ? `${process.env.REACT_APP_BACKEND_URL}${currentImage.url}` : null;
  const stockStatus = product.stock === 0 ? 'out' : product.stock <= 5 ? 'low' : 'in';

  const handleAddToCart = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      await addToCart(product.id, quantity);
      toast.success(`${quantity} x ${product.name} agregado al carrito`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar');
    }
  };

  const specs = product.specs ? product.specs.split('\n').filter(Boolean).map(line => {
    const parts = line.split(':');
    return parts.length >= 2 ? { key: parts[0].trim(), value: parts.slice(1).join(':').trim() } : { key: line, value: '' };
  }) : [];

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 gap-2"><ArrowLeft className="h-4 w-4" /> Volver</Button>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div className="space-y-3">
            <div className="aspect-square bg-secondary/50 rounded-lg overflow-hidden flex items-center justify-center">
              {imageUrl ? (
                <img src={imageUrl} alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <Package className="h-24 w-24 text-muted-foreground/20" />
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button key={img.id} onClick={() => setSelectedImage(i)} className={`w-16 h-16 rounded border-2 overflow-hidden flex-shrink-0 transition-colors duration-150 ${i === selectedImage ? 'border-foreground' : 'border-border hover:border-muted-foreground'}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="font-mono text-xs text-muted-foreground mb-2">SKU: {product.sku}</p>
              <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight mb-2">{product.name}</h1>
              {product.brand && <Badge variant="secondary">{product.brand}</Badge>}
            </div>

            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-display font-semibold" data-testid="product-price">{formatPrice(product.price)}</span>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant={stockStatus === 'out' ? 'destructive' : 'secondary'}>
                {stockStatus === 'out' ? 'Agotado' : 'Disponible'}
              </Badge>
            </div>

            {product.description && <p className="text-sm text-muted-foreground leading-relaxed">{product.description}</p>}

            {product.stock > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center border rounded-md">
                    <Button variant="ghost" size="sm" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="h-10 w-10 p-0"><Minus className="h-4 w-4" /></Button>
                    <span className="w-12 text-center font-medium">{quantity}</span>
                    <Button variant="ghost" size="sm" onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="h-10 w-10 p-0"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <Button onClick={handleAddToCart} className="flex-1 gap-2" data-testid="product-add-to-cart-button">
                    <ShoppingCart className="h-4 w-4" /> Agregar al Carrito
                  </Button>
                </div>
                
                {/* Botón de descarga de PDF */}
                {product.pdf_url && (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${product.pdf_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    download
                  >
                    <Button variant="outline" className="w-full gap-2" data-testid="download-pdf-button">
                      <Download className="h-4 w-4" />
                      Descargar Ficha Técnica (PDF)
                    </Button>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Especificaciones */}
        <div className="mt-12">
          <h2 className="text-lg font-display font-semibold mb-4">Especificaciones</h2>
          {specs.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <tbody>
                    {specs.map((s, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-secondary/30' : ''}>
                        <td className="px-4 py-2.5 font-medium w-1/3">{s.key}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : <p className="text-sm text-muted-foreground">Sin especificaciones disponibles</p>}
        </div>
      </div>
    </div>
  );
}
